import * as linspace from "@stdlib/math/utils/linspace";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import af from "dayjs/plugin/advancedFormat";

import {
  FETCH_AVAILABLE_KPIS,
  FETCH_AVAILABLE_KPIS_SUCCESS,
  FETCH_AVAILABLE_KPIS_FAILED,
  FETCH_NIGHTS_AVAILABLE,
  FETCH_NIGHTS_AVAILABLE_SUCCESS,
  FETCH_NIGHTS_AVAILABLE_FAILED,
  FETCH_KPI_DATA,
  FETCH_KPI_DATA_SUCCESS,
  FETCH_KPI_DATA_FAILED,
  FETCH_S3_KPI_DATA,
  FETCH_S3_KPI_DATA_SUCCESS,
  FETCH_S3_KPI_DATA_FAILED,
  FETCH_TV_KPI_DATA,
  FETCH_TV_KPI_DATA_SUCCESS,
  FETCH_TV_KPI_DATA_FAILED,
  RESET_TV_KPI_DATA,
  GENERATE_SYSTEM_ANNOTATIONS,
  GENERATE_SYSTEM_ANNOTATIONS_SUCCESS,
  GENERATE_SYSTEM_ANNOTATIONS_FAILED,
  GET_ML_KPI_SUMMARY,
  GET_ML_KPI_SUMMARY_SUCCESS,
  GET_ML_KPI_SUMMARY_FAILED,
  FETCH_ANNOTATIONS,
  FETCH_ANNOTATIONS_SUCCESS,
  FETCH_ANNOTATIONS_FAILED,
  FETCH_PATIENTS_RII_SUMMARY,
  FETCH_PATIENTS_RII_SUMMARY_SUCCESS,
  FETCH_PATIENTS_RII_SUMMARY_FAILED,
  DELETE_ANNOTATIONS,
  ADD_NEW_ANNOTATION,
  ADD_NEW_ANNOTATION_SUCCESS,
  ADD_NEW_ANNOTATION_FAILED,
  EDIT_ANNOTATION,
  EDIT_ANNOTATION_SUCCESS,
  EDIT_ANNOTATION_FAILED,
  FETCH_ANNOTATION_CATEGORIES,
  FETCH_ANNOTATION_CATEGORIES_SUCCESS,
  FETCH_ANNOTATION_CATEGORIES_FAILED,
  ADD_ANNOTATION_CATEGORY,
  ADD_ANNOTATION_CATEGORY_SUCCESS,
  ADD_ANNOTATION_CATEGORY_FAILED,
  EDIT_ANNOTATION_CATEGORY,
  EDIT_ANNOTATION_CATEGORY_SUCCESS,
  EDIT_ANNOTATION_CATEGORY_FAILED,
  DELETE_ANNOTATION_CATEGORY,
  DELETE_ANNOTATION_CATEGORY_SUCCESS,
  DELETE_ANNOTATION_CATEGORY_FAILED,
  UPDATE_KPI_PREFERENCE,
  UPDATE_KPI_PREFERENCE_SUCCESS,
  UPDATE_KPI_PREFERENCE_FAILED,
  UPDATE_TRENDVIEW_KPI_PREFERENCE,
  UPDATE_TRENDVIEW_KPI_PREFERENCE_SUCCESS,
  UPDATE_TRENDVIEW_KPI_PREFERENCE_FAILED,
  FETCH_PATIENT_JIRA_CARDS_DATA,
  FETCH_PATIENT_JIRA_CARDS_DATA_SUCCESS,
  FETCH_PATIENT_JIRA_CARDS_DATA_FAILED,
  EDIT_REPORT_VALUES,
  FETCH_ACTIGRAPH,
  FETCH_ACTIGRAPH_SUCCESS,
  FETCH_ACTIGRAPH_FAILED,
} from "constants/ActionTypes";

import { LOCATION_CHANGE } from "react-router-redux";

dayjs.extend(utc);
dayjs.extend(af)

const kpi_names = {
  SLP_STGS: "Sleep Stages",
  MOV: "Body Movement",
  Y_DIST: "Chest Displacement",
  BR: "Breathing Rate",
  BRV: "Breathing Rate Variability",
  HR: "Heart Rate",
  HRV: "Heart Rate Variability",
  SPO2: "SPO2",
  PR: "Pulse Rate",
  I: "I",
  Q: "Q",
  IQ: "IQ",
  SA: "Sleep Awake",
  TB: "Time in Bed",
  NC_PSG: "Nasal Cannula",
  THRX_PSG: "Thorax",
  ABD_PSG: "Abdomen",
  SPO2_PSG: "SPO2 PSG",
};

const initial_state = {
  error: null,
  is_error: false,
  reset_tv_analytics: false,
  fetching_nights_available: false,
  nights_available: null,

  analytics: null,

  available_kpis: null,
  fetching_available_kpis: false,

  fetching_ml_kpi_summary: false,

  annotations: {},
  fetching_annotations: false,
  fetching_annotations_success: false,
  fetching_annotations_failed: false,

  adding_annotation: false,
  adding_annotation_success: false,
  adding_annotation_failed: false,

  editing_annotation: false,
  editing_annotation_success: false,
  editing_annotation_failed: false,

  annotation_categories: [],
  fetching_annotation_categories: false,
  fetching_annotation_categories_success: false,
  fetching_annotation_categories_failed: false,

  adding_annotation_category: false,
  adding_annotation_category_success: false,
  adding_annotation_category_failed: false,

  editing_annotation_category: false,
  editing_annotation_category_success: false,
  editing_annotation_category_failed: false,

  deleting_annotation_category: false,
  deleting_annotation_category_success: false,
  deleting_annotation_category_failed: false,

  generating_system_annotations: false,
  generating_system_annotations_success: false,
  generating_system_annotations_failed: false,

  tv_analytics: null,
  spo2Value: false,
  fetching_tv_kpi_data: false,
  fetching_tv_kpi_data_success: false,
  fetching_tv_kpi_data_failed: false,
  update_kpi_preference: false,
  update_kpi_preference_success: false,
  update_kpi_preference_failed: false,
  update_trendview_kpi_preference: false,
  update_trendview_kpi_preference_success: false,
  update_trendview_kpi_preference_failed: false,

  fetching_patient_jira_cards_data: false,
};

const kpi_reducer = (state = initial_state, action) => {
  switch (action.type) {
    case FETCH_NIGHTS_AVAILABLE: {
      return {
        ...state,
        fetching_nights_available: true,
        nights_available: null,
        available_kpis: null,
        annotations: null,
        analytics: null,
      };
    }
    case FETCH_NIGHTS_AVAILABLE_SUCCESS: {
      return {
        ...state,
        nights_available: action.payload.nights_available,
        fetching_nights_available: false,
      };
    }
    case FETCH_NIGHTS_AVAILABLE_FAILED: {
      return {
        ...state,
        is_error: true,
        error: action.error,
        fetching_nights_available: false,
      };
    }
    case FETCH_AVAILABLE_KPIS: {
      return {
        ...state,
        fetching_available_kpis: true,
        available_kpis: null,
        analytics: null,
        annotations: null,
      };
    }
    case FETCH_AVAILABLE_KPIS_SUCCESS: {
      const available_kpis = action.payload.available_kpis;
      if (available_kpis.includes("I") || available_kpis.includes("Q")) {
        available_kpis.push("IQ");
      }

      return {
        ...state,
        available_kpis: action.payload.available_kpis,
        non_graph_available_kpis: action.payload.non_graph_available_kpis,
        fetching_available_kpis: false,
      };
    }
    case FETCH_AVAILABLE_KPIS_FAILED: {
      return {
        ...state,
        is_error: true,
        error: action.error,
        fetching_available_kpis: false,
      };
    }
    case FETCH_KPI_DATA: {
      return {
        ...state,
        fetching_kpi_data: true,
      };
    }
    case FETCH_S3_KPI_DATA: {
      return {
        ...state,
        fetching_kpi_data: true,
      };
    }
    case FETCH_KPI_DATA_SUCCESS: {
      const analytics = state.analytics || {};
      const data = action.payload.analytics;

      if (data && Object.keys(data).length > 0) {
        if (action.payload.kpi === "IQ") {
          let IQ = {
            Q: {
              dataset: [],
              ts: [],
            },
            I: {
              dataset: [],
              ts: [],
            },
          };

          Object.values(data).forEach((v) => {
            IQ[v.kpi].dataset = [...IQ[v.kpi].dataset, ...v.dataset];
            IQ[v.kpi].ts = [
              ...IQ[v.kpi].ts,
              ...linspace(
                parseInt(v.device_start_time),
                parseInt(v.device_stop_time),
                v.dataset.length
              ),
            ];
          });
          analytics.IQ = IQ;
        } else if (
          ["bp", "body_temp", "blood_sugar"].includes(action.payload.kpi)
        ) {
          // the data structure for bp, body_temp and blood_sugar is different from the other kpis
          if (action.payload.kpi === "bp") {
            let analyticsData = [],
              analyticsTs = [],
              analyticsData_upper_bp = [];
            Object.keys(data).forEach((index) => {
              let dataset = data[index].dataset;
              analyticsTs.push(data[index].device_start_time);
              analyticsData_upper_bp.push(null);
              analyticsData.push(null);

              dataset.forEach((dataset_item) => {
                analyticsData_upper_bp.push(dataset_item["upper_bp"]);
                analyticsData.push(dataset_item["lower_bp"]);
                analyticsTs.push(dataset_item["created_at"]);
              });

              analyticsTs.push(data[index].device_stop_time);
              analyticsData_upper_bp.push(null);
              analyticsData.push(null);
            });
            analytics[action.payload.kpi] = {
              ts: analyticsTs,
              dataset: analyticsData,
              dataset_systolic: analyticsData_upper_bp,
            };
          } else {
            let analyticsData = [],
              analyticsTs = [];

            Object.keys(data).forEach((index) => {
              analyticsTs.push(data[index].device_start_time);
              analyticsData.push(null);

              let dataset = data[index].dataset;
              dataset.forEach((dataset_item) => {
                analyticsData.push(dataset_item[action.payload.kpi]);
                analyticsTs.push(dataset_item["created_at"]);
              });

              analyticsTs.push(data[index].device_stop_time);
              analyticsData.push(null);
            });
            analytics[action.payload.kpi] = {
              ts: analyticsTs,
              dataset: analyticsData,
            };
          }
        } else {
          let analyticsData = [],
            analyticsTs = [];

          Object.keys(data).forEach((index) => {
            analyticsData = [...analyticsData, ...data[index].dataset];
            analyticsTs = [
              ...analyticsTs,
              ...linspace(
                parseInt(data[index].device_start_time),
                parseInt(data[index].device_stop_time),
                data[index].dataset.length
              ),
            ];
          });

          analytics[action.payload.kpi] = {
            ts: analyticsTs,
            dataset: analyticsData,
          };
        }
      } else {
        analytics[action.payload.kpi] = { ts: [], dataset: [] };
      }

      // // Below code is required for debugging kpi data
      // analytics[action.payload.kpi].ts = analytics[action.payload.kpi].ts.slice(0, 200);
      // analytics[action.payload.kpi].dataset = analytics[action.payload.kpi].dataset.slice(0, 200);

      return {
        ...state,
        analytics,
        fetching_kpi_data: false,
      };
    }
    case FETCH_S3_KPI_DATA_SUCCESS: {
      const analytics = state.analytics || {};
      const data = action.payload.data;

      if (data && data.length > 0) {
        if (action.payload.multiple) {
          if (action.payload.kpi === "IQ") {
            let IQ = {
              Q: {
                dataset: [],
                ts: [],
              },
              I: {
                dataset: [],
                ts: [],
              },
            };
            data.forEach((v) => {
              if (v.kpi === "Q" || v.kpi === "I") {
                IQ[v.kpi].dataset = [...IQ[v.kpi].dataset, ...v.device_values];
                let start_time,
                  stop_time = "";

                if (v.fs) {
                  start_time = v.t0;
                  stop_time = v.t0 + IQ[v.kpi].dataset.length / v.fs;
                } else {
                  start_time = v.recording_start_time / 1000;
                  stop_time = v.recording_stop_time / 1000;
                }
                IQ[v.kpi].ts = [
                  ...IQ[v.kpi].ts,
                  ...linspace(
                    parseInt(start_time),
                    parseInt(stop_time),
                    v.device_values.length
                  ),
                ];
              }
            });
            analytics.IQ = IQ;
          } else if (
            ["bp", "body_temp", "blood_sugar"].includes(action.payload.kpi)
          ) {
            // the data structure for bp, body_temp and blood_sugar is different from the other kpis
            if (action.payload.kpi === "bp") {
              let analyticsData = [],
                analyticsTs = [],
                analyticsData_upper_bp = [];
              Object.keys(data).forEach((index) => {
                let dataset = data[index].dataset || data[index].device_values;
                analyticsTs.push(data[index].recording_start_time);
                analyticsData_upper_bp.push(null);
                analyticsData.push(null);

                dataset.forEach((dataset_item) => {
                  analyticsData_upper_bp.push(dataset_item["upper_bp"]);
                  analyticsData.push(dataset_item["lower_bp"]);
                  analyticsTs.push(dataset_item["created_at"]);
                });

                analyticsTs.push(data[index].recording_stop_time);
                analyticsData_upper_bp.push(null);
                analyticsData.push(null);
              });
              analytics[action.payload.kpi] = {
                ts: analyticsTs,
                dataset: analyticsData,
                dataset_systolic: analyticsData_upper_bp,
              };
            } else {
              let analyticsData = [],
                analyticsTs = [];

              Object.keys(data).forEach((index) => {
                analyticsTs.push(data[index].recording_start_time);
                analyticsData.push(null);

                let dataset = data[index].dataset || data[index].device_values;
                dataset.forEach((dataset_item) => {
                  analyticsData.push(dataset_item[action.payload.kpi]);
                  analyticsTs.push(dataset_item["created_at"]);
                });

                analyticsTs.push(data[index].recording_stop_time);
                analyticsData.push(null);
              });
              analytics[action.payload.kpi] = {
                ts: analyticsTs,
                dataset: analyticsData,
              };
            }
          } else {
            // let analyticsData = [], analyticsTs = [];
            let start_time,
              stop_time = "";
            data.forEach((value) => {
              const filteredValue = value.device_values.filter((v) => v !== "");
              if (value.fs) {
                start_time = value.t0;
                stop_time = value.t0 + filteredValue.length / value.fs;
                // console.log('device-time: ', start_time, stop_time)
                analytics[value.kpi] = {
                  ts: linspace(
                    parseInt(start_time),
                    parseInt(stop_time),
                    filteredValue.length
                  ),
                  dataset: filteredValue,
                };
              } else {
                start_time = value.recording_start_time;
                stop_time = value.recording_stop_time;
                // console.log('device-time: ', start_time, stop_time)
                analytics[value.kpi] = {
                  ts: linspace(
                    parseInt(start_time / 1000),
                    parseInt(stop_time / 1000),
                    filteredValue.length
                  ),
                  dataset: filteredValue,
                };
              }
            });
            // console.log('diff in len: ', value.device_values.length, filteredValue.length)
          }
        }
        if (
          action.payload.kpi === "IQ" ||
          (Array.isArray(action.payload.kpi) &&
            action.payload.kpi.includes("IQ"))
        ) {
          let IQ = {
            Q: {
              dataset: [],
              ts: [],
            },
            I: {
              dataset: [],
              ts: [],
            },
          };

          data.forEach((v) => {
            if (v.kpi === "Q" || v.kpi === "I") {
              IQ[v.kpi].dataset = [...IQ[v.kpi].dataset, ...v.device_values];
              let start_time,
                stop_time = "";

              if (v.fs) {
                start_time = v.t0;
                stop_time = v.t0 + IQ[v.kpi].dataset.length / v.fs;
              } else {
                start_time = v.recording_start_time / 1000;
                stop_time = v.recording_stop_time / 1000;
              }

              IQ[v.kpi].ts = [
                ...IQ[v.kpi].ts,
                ...linspace(
                  parseInt(start_time),
                  parseInt(stop_time),
                  v.device_values.length
                ),
              ];
            }
          });
          analytics.IQ = IQ;
        } else if (
          ["bp", "body_temp", "blood_sugar"].includes(action.payload.kpi)
        ) {
          // the data structure for bp, body_temp and blood_sugar is different from the other kpis
          if (action.payload.kpi === "bp") {
            let analyticsData = [],
              analyticsTs = [],
              analyticsData_upper_bp = [];
            Object.keys(data).forEach((index) => {
              let dataset = data[index].dataset || data[index].device_values;
              analyticsTs.push(data[index].recording_start_time);
              analyticsData_upper_bp.push(null);
              analyticsData.push(null);

              dataset.forEach((dataset_item) => {
                analyticsData_upper_bp.push(dataset_item["upper_bp"]);
                analyticsData.push(dataset_item["lower_bp"]);
                analyticsTs.push(dataset_item["created_at"]);
              });

              analyticsTs.push(data[index].recording_stop_time);
              analyticsData_upper_bp.push(null);
              analyticsData.push(null);
            });
            analytics[action.payload.kpi] = {
              ts: analyticsTs,
              dataset: analyticsData,
              dataset_systolic: analyticsData_upper_bp,
            };
          } else {
            let analyticsData = [],
              analyticsTs = [];

            Object.keys(data).forEach((index) => {
              analyticsTs.push(data[index].recording_start_time);
              analyticsData.push(null);

              let dataset = data[index].dataset || data[index].device_values;
              dataset.forEach((dataset_item) => {
                analyticsData.push(dataset_item[action.payload.kpi]);
                analyticsTs.push(dataset_item["created_at"]);
              });

              analyticsTs.push(data[index].recording_stop_time);
              analyticsData.push(null);
            });
            analytics[action.payload.kpi] = {
              ts: analyticsTs,
              dataset: analyticsData,
            };
          }
        } else {
          // let analyticsData = [], analyticsTs = [];
          let start_time,
            stop_time = "";

          data.forEach((value) => {
            let add = false;
            if (action.payload.multiple) {
              add = action.payload.kpi.includes(value.kpi);
            } else {
              add = value.kpi === action.payload.kpi;
            }
            if (add) {
              const filteredValue = value.device_values.filter((v) => v !== "");
              if (value.fs) {
                start_time = value.t0;
                stop_time = value.t0 + filteredValue.length / value.fs;
                // console.log('device-time: ', start_time, stop_time)
                analytics[value.kpi] = {
                  ts: linspace(
                    parseInt(start_time),
                    parseInt(stop_time),
                    filteredValue.length
                  ),
                  dataset: filteredValue,
                };
              } else {
                start_time = value.recording_start_time;
                stop_time = value.recording_stop_time;
                // console.log('device-time: ', start_time, stop_time)
                analytics[value.kpi] = {
                  ts: linspace(
                    parseInt(start_time / 1000),
                    parseInt(stop_time / 1000),
                    filteredValue.length
                  ),
                  dataset: filteredValue,
                };
              }
            }
          });
        }
      } else {
        analytics[action.payload.kpi] = { ts: [], dataset: [] };
      }

      // // Below code is required for debugging kpi data
      // analytics[action.payload.kpi].ts = analytics[action.payload.kpi].ts.slice(0, 200);
      // analytics[action.payload.kpi].dataset = analytics[action.payload.kpi].dataset.slice(0, 200);
      return {
        ...state,
        analytics,
        recording_meta: action.payload.recording_meta,
        fetching_kpi_data: false,
      };
    }
    case FETCH_KPI_DATA_FAILED: {
      return {
        ...state,
        is_error: true,
        error: action.error,
        fetching_kpi_data: false,
      };
    }
    case FETCH_S3_KPI_DATA_FAILED: {
      return {
        ...state,
        is_error: true,
        error: action.error,
        fetching_kpi_data: false,
      };
    }

    case FETCH_TV_KPI_DATA: {
      return {
        ...state,
        tv_analytics: {
          ...state.tv_analytics, // Why we are updating this ?
        },
        spo2Value: false,
        fetching_tv_kpi_data: true,
        fetching_tv_kpi_data_success: false,
        fetching_tv_kpi_data_failed: false,
      };
    }
    case FETCH_TV_KPI_DATA_SUCCESS: {
      const tv_analytics = state.tv_analytics || [];
      let data = action.payload.analytics;

      if (data && data.length > 0) {
        let firstDate;
        let lastDate;

        const getDates = (sDate, eDate) => {
          let dateArray = [];
          let startDate = dayjs(sDate);
          let stopDate = dayjs(eDate);
          while (startDate <= stopDate) {
            dateArray.push(
              dayjs
                .utc(dayjs(startDate).format("YYYY-MM-DD 00:00:00"))
                .valueOf()
            );
            startDate = dayjs(startDate).add(1, "days");
          }
          return dateArray;
        };
        const allDates = getDates(
          parseInt(action.payload.device_start_time) * 1000,
          parseInt(action.payload.device_stop_time) * 1000
        );
        // console.log(' all dates ', action.payload.kpi , allDates)
        data.forEach((d) => {
          const converted_date = d.recording_date.split("-");
          if (converted_date.length > 3) {
            converted_date.pop();
          }
          let dateTime;
          if (
            ["BR", "HR", "AHI", "MOV", "SPO2", "PR"].indexOf(
              action.payload.kpi
            ) > -1
          ) {
            dateTime = dayjs
              .utc(converted_date.join("-"))
              .format("YYYY-MM-DD HH:mm:ss");
          } else if (["SQ"].indexOf(action.payload.kpi) > -1) {
            dateTime = dayjs
              .utc(converted_date.join("-"))
              .format("YYYY-MM-DD HH:mm:ss");
          } else if (["RII", "SE", "ODI"].indexOf(action.payload.kpi) > -1) {
            dateTime = dayjs
              .utc(converted_date.join("-"))
              .format("YYYY-MM-DD HH:mm:ss");
            if (d.rii && !isNaN(d.rii) && !(d.rii === "Infinity")) {
              d.rii = JSON.parse(d.rii).toFixed(2);
            }
          } else if (
            ["bp", "blood_sugar", "body_temp", "weight"].indexOf(
              action.payload.kpi
            ) > -1
          ) {
            dateTime = dayjs
              .utc(converted_date.join("-"))
              .format("YYYY-MM-DD HH:mm:ss");
          }

          let date;
          if (dateTime) {
            date = dayjs.utc(dateTime).format("YYYY-MM-DD");
          }

          d.date = dayjs.utc(date).valueOf();
        });

        let newData = [];
        allDates.forEach((v) => {
          const foundData = data.find((d) => d.date === v);
          if (foundData) {
            newData.push(foundData);
          } else {
            newData.push({ date: v });
          }
        });

        newData.forEach((d, i) => {
          if (i === 0) {
            firstDate = dayjs
              .utc(d.date)
              .subtract(8, "h")
              .valueOf();
          }
          if (i === newData.length - 1) {
            lastDate = dayjs
              .utc(d.date)
              .add(8, "h")
              .valueOf();
          }
        });

        // console.log(' newData ', action.payload.kpi, newData)
        newData = [
          {
            date: firstDate,
          },
          ...newData,
          {
            date: lastDate,
          },
        ];

        tv_analytics[action.payload.kpi] = newData;
      } else {
        tv_analytics[action.payload.kpi] = [];
      }

      // console.log(' tv_analytics for kpi ', action.payload.kpi , ' is ', tv_analytics[action.payload.kpi]);

      return {
        ...state,
        tv_analytics,
        spo2Value: false,
        fetching_tv_kpi_data: false,
        fetching_tv_kpi_data_success: true,
        fetching_tv_kpi_data_failed: false,
      };
    }

    case FETCH_ACTIGRAPH: {
      return {
        ...state,
        actigraph: {
          ...state.actigraph, // Why we are updating this ?
        },
        spo2Value: false,
        fetching_actigraph: true,
        fetching_actigraph_success: false,
        fetching_actigraph_failed: false,
      };
    }

    case FETCH_ACTIGRAPH_SUCCESS: {
      let data = action.payload.actigraph;
      let actigraph_dates = [],
        actigraph = {};
    //   const format = "DD-MM-YYYY HH:mm:ss";
      let graph_min, graph_max;
      // t0 + (device_values.length / frequency) - already in seconds

      Object.keys(data).forEach((k, dayIdx) => {
        const sa_data = data[k].find((d) => d.kpi === "SA");
        const acti_data = data[k].find((d) => d.kpi === "movement_density");
        if (!sa_data || !acti_data) {
          return;
        }
        const sleep_bands = [],
          awake_bands = [],
          undefined_bands = [];
        actigraph_dates.push(k);
        // let start_time = dayjs
        //   .unix(
        //     (acti_data.recording_start_time ||
        //       acti_data.meta_data.recording_start_time) / 1000
        //   )
        //   .format(format);
        // let stop_time = dayjs
        //   .unix(
        //     (acti_data.recording_stop_time ||
        //       acti_data.meta_data.recording_stop_time) / 1000
        //   )
        //   .format(format);
        const duration_in_seconds = dayjs(acti_data.recording_stop_time ||
            acti_data.meta_data.recording_stop_time).diff(
          dayjs(acti_data.recording_start_time ||
            acti_data.meta_data.recording_start_time),
          "seconds"
        );
        const line_data = [];
        const recording_start_clock_time = dayjs(acti_data.recording_start_time ||
            acti_data.meta_data.recording_start_time)
          .subtract(dayIdx, "day")
          .valueOf();
        const recording_stop_clock_time = dayjs(acti_data.recording_stop_time ||
            acti_data.meta_data.recording_stop_time)
          .subtract(dayIdx, "day")
          .valueOf();

        if (!graph_min || graph_min > recording_start_clock_time) {
          graph_min = recording_start_clock_time;
        }
        if (!graph_max || graph_max < recording_stop_clock_time) {
          graph_max = recording_stop_clock_time;
        }

        // get epoch value from moment - 31-05-2021 18:05:03
        const epoch =
          dayjs(acti_data.recording_start_time ||
            acti_data.meta_data.recording_start_time)
            .subtract(dayIdx, "days")
            .valueOf()/1000;
        let start_epoch, awake_start_epoch, undefined_start_epoch;
        // console.log('duration in seconds: ', {
        //   duration_in_seconds,
        //   start_time,
        //   stop_time,
        //   'acti_data.device_values.length': acti_data.device_values.length,
        //   'acti_data.recording_start_time': acti_data.recording_start_time,
        //   'acti_data.recording_stop_time': acti_data.recording_stop_time,
        //   'sa_data.device_values.length': sa_data.device_values.length,
        // });
        let increment_count, acti_increment_count;
        // let start_time_sa = start_time,
          // stop_time_sa = stop_time,
        //   start_time_acti = start_time,
          // stop_time_acti = stop_time,
        let epoch_sa = epoch/1000,
          epoch_acti = epoch/1000;
        if (sa_data.fs) {
          increment_count = Math.round(1 / sa_data.fs);
          // const t1 =
          //   sa_data.t0 + sa_data.device_values.length * increment_count;
        //   start_time_sa = dayjs(sa_data.t0).format(format);
          // stop_time_sa = moment.unix(t1).format(format);
          // const duration_in_seconds_sa = moment(stop_time_sa, format).diff(
          //   moment(start_time_sa, format),
          //   'seconds',
          // );

          // const t1_acti =
          //   acti_data.t0 + acti_data.device_values.length * increment_count;
          epoch_sa =
          dayjs(sa_data.t0 * 1000)
          .subtract(dayIdx, "day")
          .valueOf()/1000;
          console.log('sa_data.t0: ', sa_data.t0, epoch_sa)
          acti_increment_count = Math.round(1 / acti_data.fs);

        //   start_time_acti = dayjs(acti_data.t0).format(format);
          // stop_time_acti = moment.unix(t1_acti).format(format);
          epoch_acti =
            dayjs(acti_data.t0)
              .subtract(dayIdx, "days")
              .valueOf()/1000;
        } else {
          increment_count = duration_in_seconds / sa_data.device_values.length;
          acti_increment_count = 1;
        }
        // console.groupCollapsed('check');
        if (acti_data.device_values.length === sa_data.device_values.length) {
          for (let i = 0; i < acti_data.device_values.length; i++) {
            const offset_seconds = i;
            if (graph_max < (epoch + offset_seconds) * 1000) {
              graph_max = (epoch + offset_seconds) * 1000;
            }
            if (graph_min > (epoch + offset_seconds) * 1000) {
              graph_min = (epoch + offset_seconds) * 1000;
            }
            line_data.push([
              (epoch + offset_seconds) * 1000,
              acti_data.device_values[i],
            ]);
            if (!start_epoch && sa_data.device_values[i] === 0) {
              // console.log(`setting start epoch - index: ${i} `);
              start_epoch = (epoch + offset_seconds) * 1000;
            }

            if (
              start_epoch &&
              (sa_data.device_values[i] === 1 ||
                sa_data.device_values[i] === null ||
                sa_data.device_values[i] === undefined)
            ) {
              // console.log(`closing sleep band - index: ${i} `);
              sleep_bands.push({
                from: start_epoch,
                to: (epoch + offset_seconds - 1) * 1000,
              });
              start_epoch = null;
            }

            if (!awake_start_epoch && sa_data.device_values[i] === 1) {
              // console.log(`awake start epoch - index: ${i}`);
              awake_start_epoch = (epoch + offset_seconds) * 1000;
            }

            if (
              awake_start_epoch &&
              (sa_data.device_values[i] === 0 ||
                sa_data.device_values[i] === null ||
                sa_data.device_values[i] === undefined)
            ) {
              // console.log(`awake stop  epoch - index: ${i}`);
              awake_bands.push({
                from: awake_start_epoch,
                to: (epoch + offset_seconds - 1 * increment_count) * 1000,
              });
              awake_start_epoch = null;
            }

            if (
              !undefined_start_epoch &&
              (sa_data.device_values[i] === null ||
                sa_data.device_values[i] === undefined)
            ) {
              // console.log(`undefined start epoch - index: ${i}`);
              undefined_start_epoch = (epoch + offset_seconds) * 1000;
            }

            if (
              undefined_start_epoch &&
              (sa_data.device_values[i] === 0 || sa_data.device_values[i] === 1)
            ) {
              // console.log(`undefined close epoch - index: ${i}`);
              undefined_bands.push({
                from: undefined_start_epoch,
                to: (epoch + offset_seconds - 1 * increment_count) * 1000,
              });
              undefined_start_epoch = null;
            }

            // console.log('check-values: ', {
            //   awake_start_epoch,
            //   undefined_start_epoch,
            //   start_epoch,
            //   index: i,
            //   value: sa_data.device_values[i],
            // });
          }
        } else {
          for (let i = 0; i < acti_data.device_values.length; i++) {
            line_data.push([
              (epoch_acti * 1000 + acti_increment_count) * 1000,
              acti_data.device_values[i],
            ]);
          }

          for (let i = 0; i < sa_data.device_values.length; i++) {
            const offset_seconds = i * increment_count;
            // console.log('offset-seconds: ', offset_seconds);
            if (graph_max < (epoch_sa + offset_seconds) * 1000) {
              graph_max = (epoch_sa + offset_seconds) * 1000;
            }
            if (graph_min > (epoch_sa + offset_seconds) * 1000) {
              graph_min = (epoch_sa + offset_seconds) * 1000;
            }
            if (!start_epoch && sa_data.device_values[i] === 0) {
              // console.log(`setting start epoch_sa - index: ${i} `);
              start_epoch = (epoch_sa + offset_seconds) * 1000;
            }

            if (
              start_epoch &&
              (sa_data.device_values[i] === 1 ||
                sa_data.device_values[i] === null ||
                sa_data.device_values[i] === undefined)
            ) {
              // console.log(`closing sleep band - index: ${i} `);
              sleep_bands.push({
                from: start_epoch,
                to: (epoch_sa + offset_seconds - 1) * 1000,
              });
              start_epoch = null;
            }

            if (!awake_start_epoch && sa_data.device_values[i] === 1) {
              // console.log(`awake start epoch_sa - index: ${i}`);
              awake_start_epoch = (epoch_sa + offset_seconds) * 1000;
            }

            if (
              awake_start_epoch &&
              (sa_data.device_values[i] === 0 ||
                sa_data.device_values[i] === null ||
                sa_data.device_values[i] === undefined)
            ) {
              // console.log(`awake stop  epoch_sa - index: ${i}`);
              awake_bands.push({
                from: awake_start_epoch,
                to: (epoch_sa + offset_seconds - 1 * increment_count) * 1000,
              });
              awake_start_epoch = null;
            }

            if (
              !undefined_start_epoch &&
              (sa_data.device_values[i] === null ||
                sa_data.device_values[i] === undefined)
            ) {
              // console.log(`undefined start epoch_sa - index: ${i}`);
              undefined_start_epoch = (epoch_sa + offset_seconds) * 1000;
            }

            if (
              undefined_start_epoch &&
              (sa_data.device_values[i] === 0 || sa_data.device_values[i] === 1)
            ) {
              // console.log(`undefined close epoch_sa - index: ${i}`);
              undefined_bands.push({
                from: undefined_start_epoch,
                to: (epoch_sa + offset_seconds - 1 * increment_count) * 1000,
              });
              undefined_start_epoch = null;
            }

            // console.log('check-values: ', {
            //   awake_start_epoch,
            //   undefined_start_epoch,
            //   start_epoch,
            //   index: i,
            //   value: sa_data.device_values[i],
            // });
          }
        }
        // console.groupEnd('check');

        // console.log('bands: ', { sleep_bands, awake_bands, undefined_bands });

        actigraph[k] = {
          line_data,
          meta_data: acti_data.meta_data,
          sleep_bands,
          awake_bands,
          undefined_bands,
        };
      });

      Object.keys(actigraph).forEach((k) => {
        actigraph[k].meta_data.graph_start_time = graph_min;
        actigraph[k].meta_data.graph_stop_time = graph_max;
        // console.log({ graph_max, graph_min });
      });
      // console.log(' tv_analytics for kpi ', action.payload.kpi , ' is ', tv_analytics[action.payload.kpi]);

      return {
        ...state,
        actigraph,
        actigraph_dates,
        actigraph_meta: action.payload.actigraph_meta,
        spo2Value: false,
        fetching_actigraph: false,
        fetching_actigraph_success: true,
        fetching_actigraph_failed: false,
      };
    }

    case FETCH_ACTIGRAPH_FAILED: {
      return {
        ...state,
        fetching_actigraph: false,
        fetching_actigraph_success: false,
        fetching_actigraph_failed: true,
      };
    }

    case EDIT_REPORT_VALUES: {
      let updatedValues = action.payload;
      const tv_analytics = updatedValues ? updatedValues : state.tv_analytics;
      return {
        ...state,
        tv_analytics,
        spo2Value: action.payload.spo2Value,
      };
    }

    case FETCH_TV_KPI_DATA_FAILED: {
      return {
        ...state,
        fetching_tv_kpi_data: false,
        fetching_tv_kpi_data_success: false,
        fetching_tv_kpi_data_failed: true,
      };
    }

    case RESET_TV_KPI_DATA: {
      if (action.payload) {
        var tv_analytics;

        if (action.payload === "omitSE") {
          tv_analytics = {};
          var reset_tv_analytics = true;
        } else {
          tv_analytics = state.tv_analytics;
          tv_analytics[action.payload] = [];
        }
        return {
          ...state,
          tv_analytics,
          spo2Value: false,
          reset_tv_analytics,
        };
      }

      return {
        ...state,
        tv_analytics: {
          RII: [],
          BR: [],
          HR: [],
          SQ: [],
          SE: [],
          SPO2: [],
          PR: [],
          bp: [],
          blood_sugar: [],
          body_temp: [],
          weight: [],
        },
      };
    }

    case GET_ML_KPI_SUMMARY: {
      return {
        ...state,
        fetching_ml_kpi_summary: true,
        ml_kpis: action.payload,
      };
    }
    case GET_ML_KPI_SUMMARY_SUCCESS: {
      return {
        ...state,
        fetching_ml_kpi_summary: false,
        ml_kpis: action.payload,
      };
    }
    case GET_ML_KPI_SUMMARY_FAILED: {
      return {
        ...state,
        fetching_ml_kpi_summary: false,
        is_error: true,
        error: action.error,
        fetching_kpi_data: false,
      };
    }

    case FETCH_ANNOTATIONS: {
      return {
        ...state,
        fetching_annotations: true,
      };
    }

    case FETCH_PATIENTS_RII_SUMMARY: {
      return {
        ...state,
      };
    }
    case FETCH_PATIENTS_RII_SUMMARY_SUCCESS: {
      return {
        ...state,
        fetch_patients_rii_summary_data: action.payload,
      };
    }

    case FETCH_PATIENTS_RII_SUMMARY_FAILED: {
      return {
        ...state,
      };
    }

    case DELETE_ANNOTATIONS: {
      return {
        ...state,
        fetching_annotations: true,
      };
    }

    case FETCH_ANNOTATIONS_SUCCESS: {
      const annotations = [...action.payload.annotations];
      const filtered_annotations = annotations.filter((a) => a.label_id);

      const a_list = filtered_annotations
        .map((fa) => {
          if (!fa.kpi || !fa.duration) {
            return null;
          }

          const faKpiList = fa.kpi.split(",");
          const kpiNamesKeyList = Object.keys(kpi_names);
          const nonCommonfaKpiList = faKpiList.filter(
            (value) => !kpiNamesKeyList.includes(value)
          );
          if (nonCommonfaKpiList.length > 0) {
            return null;
          }

          const isValidDuration = /^(?:(?:([01]?\d|2[0-3]):)?([0-5]?\d):)?([0-5]?\d)$/.test(
            fa.duration
          );
          if (!isValidDuration) {
            return null;
          }

          const kpiList = {};
          faKpiList.forEach((k) => {
            if (kpi_names[k]) {
              kpiList[k] = kpi_names[k];
            }
          });

          const faData = {
            ...fa,
            kpiList: kpiList,
          };

          return faData;
        })
        .filter((fa) => fa);

      return {
        ...state,
        no_system_annotations: action.payload.no_system_annotations,
        no_system_desaturations: action.payload.no_system_desaturations,
        fetching_annotations: false,
        annotations: [...a_list],
        fetching_annotations_success: true,
        fetching_annotations_failed: false,
      };
    }

    case FETCH_ANNOTATIONS_FAILED: {
      return {
        ...state,
        fetching_annotations: false,
        fetching_annotations_success: false,
        fetching_annotations_failed: true,
      };
    }

    case ADD_NEW_ANNOTATION: {
      return {
        ...state,
        adding_annotation: true,
      };
    }

    case ADD_NEW_ANNOTATION_SUCCESS: {
      return {
        ...state,
        adding_annotation: false,
        adding_annotation_success: true,
        adding_annotation_failed: false,
      };
    }

    case ADD_NEW_ANNOTATION_FAILED: {
      return {
        ...state,
        adding_annotation: false,
        adding_annotation_success: false,
        adding_annotation_failed: true,
      };
    }

    case EDIT_ANNOTATION: {
      return {
        ...state,
        editing_annotation: true,
      };
    }

    case EDIT_ANNOTATION_SUCCESS: {
      return {
        ...state,
        editing_annotation: false,
        editing_annotation_success: true,
        editing_annotation_failed: false,
      };
    }

    case EDIT_ANNOTATION_FAILED: {
      return {
        ...state,
        editing_annotation: false,
        editing_annotation_success: false,
        editing_annotation_failed: true,
      };
    }

    case FETCH_ANNOTATION_CATEGORIES: {
      return {
        ...state,
        fetching_annotation_categories: true,
      };
    }

    case FETCH_ANNOTATION_CATEGORIES_SUCCESS: {
      return {
        ...state,
        fetching_annotation_categories: false,
        annotation_categories: [...action.payload.annotation_categories],
        fetching_annotation_categories_success: true,
        fetching_annotation_categories_failed: false,
      };
    }

    case FETCH_ANNOTATION_CATEGORIES_FAILED: {
      return {
        ...state,
        fetching_annotation_categories: false,
        fetching_annotation_categories_success: false,
        fetching_annotation_categories_failed: true,
      };
    }

    case ADD_ANNOTATION_CATEGORY: {
      return {
        ...state,
        adding_annotation_category: true,
      };
    }

    case ADD_ANNOTATION_CATEGORY_SUCCESS: {
      return {
        ...state,
        adding_annotation_category: false,
        adding_annotation_category_success: true,
        adding_annotation_category_failed: false,
      };
    }

    case ADD_ANNOTATION_CATEGORY_FAILED: {
      return {
        ...state,
        adding_annotation_category: false,
        adding_annotation_category_success: false,
        adding_annotation_category_failed: true,
      };
    }

    case EDIT_ANNOTATION_CATEGORY: {
      return {
        ...state,
        editing_annotation_category: true,
      };
    }

    case EDIT_ANNOTATION_CATEGORY_SUCCESS: {
      return {
        ...state,
        editing_annotation_category: false,
        editing_annotation_category_success: true,
        editing_annotation_category_failed: false,
      };
    }

    case EDIT_ANNOTATION_CATEGORY_FAILED: {
      return {
        ...state,
        editing_annotation_category: false,
        editing_annotation_category_success: false,
        editing_annotation_category_failed: true,
      };
    }

    case DELETE_ANNOTATION_CATEGORY: {
      return {
        ...state,
        deleting_annotation_category: true,
      };
    }

    case DELETE_ANNOTATION_CATEGORY_SUCCESS: {
      return {
        ...state,
        deleting_annotation_category: false,
        deleting_annotation_category_success: true,
        deleting_annotation_category_failed: false,
      };
    }

    case DELETE_ANNOTATION_CATEGORY_FAILED: {
      return {
        ...state,
        deleting_annotation_category: false,
        deleting_annotation_category_success: false,
        deleting_annotation_category_failed: true,
      };
    }

    case GENERATE_SYSTEM_ANNOTATIONS: {
      return {
        ...state,
        generating_system_annotations: true,
      };
    }

    case GENERATE_SYSTEM_ANNOTATIONS_SUCCESS: {
      return {
        ...state,
        generating_system_annotations: false,
        generating_system_annotations_success: true,
        generating_system_annotations_failed: false,
      };
    }

    case GENERATE_SYSTEM_ANNOTATIONS_FAILED: {
      return {
        ...state,
        generating_system_annotations: false,
        generating_system_annotations_success: false,
        generating_system_annotations_failed: true,
      };
    }

    case LOCATION_CHANGE: {
      return {
        ...initial_state,
      };
    }

    case UPDATE_KPI_PREFERENCE: {
      return {
        ...state,
        update_kpi_preference: true,
      };
    }

    case UPDATE_KPI_PREFERENCE_SUCCESS: {
      return {
        ...state,
        update_kpi_preference: false,
        update_kpi_preference_success: true,
        update_kpi_preference_failed: false,
      };
    }

    case UPDATE_KPI_PREFERENCE_FAILED: {
      return {
        ...state,
        update_kpi_preference: false,
        update_kpi_preference_success: false,
        update_kpi_preference_failed: true,
      };
    }
    case UPDATE_TRENDVIEW_KPI_PREFERENCE: {
      return {
        ...state,
        update_trendview_kpi_preference: true,
      };
    }
    case UPDATE_TRENDVIEW_KPI_PREFERENCE_SUCCESS: {
      return {
        ...state,
        update_trendview_kpi_preference: false,
        update_trendview_kpi_preference_success: true,
        update_trendview_kpi_preference_failed: false,
      };
    }
    case UPDATE_TRENDVIEW_KPI_PREFERENCE_FAILED: {
      return {
        ...state,
        update_trendview_kpi_preference: false,
        update_trendview_kpi_preference_success: false,
        update_trendview_kpi_preference_failed: true,
      };
    }

    case FETCH_PATIENT_JIRA_CARDS_DATA: {
      return {
        ...state,
        fetching_patient_jira_cards_data: true,
      };
    }
    case FETCH_PATIENT_JIRA_CARDS_DATA_SUCCESS: {
      return {
        ...state,
        jira_cards_data: action.payload,
        ...action.payload,
        fetching_patient_jira_cards_data: false,
      };
    }
    case FETCH_PATIENT_JIRA_CARDS_DATA_FAILED: {
      return {
        ...state,
        is_error: true,
        error: action.error,
        fetching_patient_jira_cards_data: false,
      };
    }

    default:
      return state;
  }
};

export default kpi_reducer;
